import { createHash } from 'node:crypto';
import JSZip from 'jszip';

/** One file inside a skill archive: a repo-relative posix path + its raw bytes. */
export interface ArchiveEntry {
  path: string;
  data: Uint8Array;
}

/** A packed archive plus its content-address (sha256 over the archive bytes). */
export interface PackedArchive {
  bytes: Uint8Array;
  sha256: string;
  entryPaths: string[];
}

/** Zip-slip guard, mirroring the fetcher's `isUnsafeEntryPath`. Backslashes are
 *  folded to `/` first so a crafted `refs\..\evil` cannot slip its `..` past us. */
function isUnsafeArchivePath(rel: string): boolean {
  const n = rel.replace(/\\/g, '/');
  if (n.includes('\0')) return true;
  if (n.startsWith('/')) return true;
  return n.split('/').includes('..');
}

/** sha256 hex digest of raw bytes — the CAS key for a skill archive. */
export function sha256Hex(bytes: Uint8Array): string {
  return createHash('sha256').update(bytes).digest('hex');
}

/**
 * Pack skill files into ONE deterministic compressed archive and hash it.
 *
 * Determinism is load-bearing: the CAS layer dedups by sha256, so the SAME
 * source tree MUST produce the SAME bytes. jszip's per-file date defaults to
 * `new Date()` (non-deterministic); we pin every entry to the epoch and sort
 * entries by path before adding, so identical inputs pack to identical bytes
 * for a given jszip version (pinned ^3.10.1). A jszip bump would shift shas
 * fleet-wide — a dedup miss (new objects), never a correctness bug.
 */
export async function packSkillArchive(entries: ArchiveEntry[]): Promise<PackedArchive> {
  const sorted = [...entries].sort((a, b) => a.path.localeCompare(b.path));
  const zip = new JSZip();
  for (const e of sorted) {
    zip.file(e.path, e.data, { date: new Date(0), binary: true, createFolders: false });
  }
  const bytes = await zip.generateAsync({
    type: 'uint8array',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
    platform: 'UNIX',
  });
  return { bytes, sha256: sha256Hex(bytes), entryPaths: sorted.map((e) => e.path) };
}

/**
 * Unpack an archive back to entries. Directory records and any zip-slip path
 * are dropped (defense in depth — the writer already validated at pack time).
 * Output is path-sorted so materialize writes in a stable order.
 */
export async function unpackSkillArchive(bytes: Uint8Array): Promise<ArchiveEntry[]> {
  const zip = await JSZip.loadAsync(bytes);
  const out: ArchiveEntry[] = [];
  for (const entry of Object.values(zip.files)) {
    if (entry.dir) continue;
    if (isUnsafeArchivePath(entry.name)) continue;
    const data = await entry.async('uint8array');
    out.push({ path: entry.name, data });
  }
  out.sort((a, b) => a.path.localeCompare(b.path));
  return out;
}
